import { useQuery } from 'react-query';
import { useMemo } from 'react';
import unknownErrorToString from '../lib/unknownErrorToString';
import apiPath from '../api/apiPath.ts';

const fetchBotPagesConfig = async (): Promise<BotPagesConfig> => {
    const response = await fetch(apiPath('api/bot-pages-config'));

    if (!response.ok) {
        throw new Error('Network response was not ok');
    }

    return response.json();
};

export enum EngagementAttachmentType {
    TextOnly,
    Buttons,
    VideoCarousel,
    SuggestedActions
}

export type Engagement = {
    attachmentMessage: string;
    attachmentType: EngagementAttachmentType;
    attachments: string;
    initialMessage: string;
};

export type BotPagesConfig = {
    clientId: string;
    tenantId: string;
    subscriptionKey: string;
    portalScriptUrl: string;
    title: string;
    pageHeader: string;
    pageSubHeader: string;
    homePageUrl: string;
    goToHomePageTitle: string;
    externalDomain: string;
    internalSubDomain: string;
    welcomeEngagement: Engagement;
    styles: Record<string, string>;
    hasArticles: boolean;
    requiresAuthentication: boolean;
    isLoading: boolean;
    error: string | null;
    hasErrors: boolean;
};

const defaultBotPagesConfig: BotPagesConfig = {
    clientId: '',
    tenantId: '',
    subscriptionKey: '',
    portalScriptUrl: '',
    externalDomain: '',
    goToHomePageTitle: '',
    hasArticles: false,
    homePageUrl: '',
    internalSubDomain: '',
    pageHeader: '',
    pageSubHeader: '',
    requiresAuthentication: false,
    styles: {},
    title: '',
    welcomeEngagement: {
        attachmentMessage: '',
        attachmentType: EngagementAttachmentType.TextOnly,
        attachments: '',
        initialMessage: ''
    },
    isLoading: true,
    error: null,
    hasErrors: false
};

const useBotPagesConfig = (): BotPagesConfig => {
    const { data, isLoading, error } = useQuery(
        'botPagesConfig',
        fetchBotPagesConfig,
        {
            staleTime: Infinity // Prevents refetching
        }
    );

    return useMemo(() => {
        if (isLoading || error) {
            return {
                ...defaultBotPagesConfig,
                isLoading,
                error: error ? unknownErrorToString(error) : null
            };
        }

        return {
            ...defaultBotPagesConfig,
            ...data,
            isLoading: false,
            error: null
        };
    }, [data, isLoading, error]);
};

export default useBotPagesConfig;
